import { authUserService } from '@store/AuthUser/services/authUserService';
import compact from 'lodash/compact';
import flatten from 'lodash/flatten';
import uniq from 'lodash/uniq';
import difference from 'lodash/difference';

import {
    SortingOrder,
    TaskCreationParams,
    TaskData,
    TaskListQuery,
    TaskListResponse,
    TaskStatus,
    TaskUpdateParams,
} from 'chaika-frontend-types/frontend-cms';

import { MedicalCaseApi } from '@api/MedicalCaseApi';
import { TaskApi } from '@api/TaskApi';

import { store } from '@store';
import { getUniqNewChunkedIds } from '@utils/getUniqNewChunkedIds';
import { patientService } from '@modules/patient/services/patient/patientService';
import { TaskStore, taskStore } from '@store/Task';
import { getByFieldName } from '@utils/getByFieldName';
import { loadWithChunkAndFlatten } from '@utils/loadWithChunkAndFlatten';

class TaskService {
    private readonly taskStore: TaskStore = taskStore;
    // `add` дописывает в тот же Map, поэтому ответ отменённой загрузки склеился бы со списком актуальной.
    private lastTasksLoadId = 0;

    public async loadTaskById(taskId: string): Promise<void> {
        const task = await TaskApi.getById(taskId);
        this.taskStore.taskList.addIfNotExist([task]);
        await this.loadTaskData([task]);
    }

    public async loadTaskByUserIds(userIds: string[]): Promise<void> {
        const userIdsString = userIds.join(',');
        await this.loadTaskByUserId(userIdsString);
    }

    public async loadTaskByUserId(userId: string): Promise<void> {
        const tasks = await Promise.all([
            TaskApi.find({ authorId: userId }),
            TaskApi.find({ requesterId: userId }),
            TaskApi.find({ executorId: userId }),
        ]);

        const flattenTasks = flatten(tasks);

        this.taskStore.taskList.add(flattenTasks);
        await this.loadTaskData(flattenTasks);
    }

    public async loadTasksByExecutorIds(
        executorIds: string[],
        params?: Omit<TaskListQuery, 'executorId'>,
        withoutAdditionalData?: boolean,
    ): Promise<void> {
        const tasks = await TaskApi.find({ executorId: executorIds.join(), ...params });

        this.taskStore.taskList.add(tasks);

        if (!withoutAdditionalData) {
            await this.loadTaskData(tasks);
        }
    }

    public async updateStartSortingOrder(order: SortingOrder): Promise<void> {
        await this.loadTasks({
            ...this.taskStore.taskList.searchParams,
            startSortingOrder: order,
        });
    }

    public async loadTasks(params: TaskListQuery): Promise<TaskListResponse> {
        const loadId = ++this.lastTasksLoadId;

        this.taskStore.taskList.reset();

        const { app } = store;
        app.setIsOnUpdate(true);

        this.taskStore.taskList.setSearchParams(params);
        const { tasks, count } = await TaskApi.findWithCount({
            limit: this.taskStore.taskList.limit,
            ...params,
        });

        await this.loadTaskData(tasks);

        if (this.isOutdatedTasksLoad(loadId)) {
            return { tasks, count };
        }

        this.taskStore.taskList.addNext(tasks);
        this.taskStore.taskList.count = count;

        app.setIsOnUpdate(false);

        return { tasks, count };
    }

    public async loadNextTasks(): Promise<void> {
        const { app } = store;
        if (!app.isOnUpdate && this.taskStore.taskList.hasNext) {
            const loadId = this.lastTasksLoadId;

            app.setIsOnUpdate(true);
            const { searchParams } = this.taskStore.taskList;
            const { limit, offset } = this.taskStore.taskList;
            const tasks = await TaskApi.find({ ...searchParams, limit, offset });

            if (this.isOutdatedTasksLoad(loadId)) {
                return;
            }

            this.taskStore.taskList.addNext(tasks);
            await this.loadTaskData(tasks);
            app.setIsOnUpdate(false);
        }
    }

    public async loadTaskByMedicalCaseId(medicalCaseId: string): Promise<void> {
        if (!this.taskStore.taskList.isTaskLoadedForMedicalCase(medicalCaseId)) {
            this.taskStore.taskList.addTaskLoadedMedicalCaseId(medicalCaseId);
            const tasks = await TaskApi.find({ medicalCaseId });
            this.taskStore.taskList.add(tasks);
            await this.loadTaskData(tasks);
        }
    }

    public async loadTaskByMedicalCaseIds(medicalCaseIds: string[]): Promise<void> {
        const notLoadedIds = medicalCaseIds.filter(id => !this.taskStore.taskList.isTaskLoadedForMedicalCase(id));
        if (notLoadedIds.length > 0) {
            this.taskStore.taskList.addTaskLoadedMedicalCaseIds(notLoadedIds);
            const tasks: TaskData[] = await loadWithChunkAndFlatten<TaskData>(
                notLoadedIds,
                TaskApi.find,
                'medicalCaseId',
            );
            this.taskStore.taskList.add(tasks);
            await this.loadTaskData(tasks);
        }
    }

    public async loadTaskByPatientId(patientId: string): Promise<void> {
        if (!this.taskStore.taskList.isTaskLoadedForPatient(patientId)) {
            this.taskStore.taskList.addTaskLoadedPatientId(patientId);
            const tasks = await TaskApi.find({ patientId });
            this.taskStore.taskList.add(tasks);
            await this.loadTaskData(tasks);
        }
    }

    public async create(creationParams: TaskCreationParams): Promise<void> {
        const timezone = creationParams.timezone ?? authUserService.getTimezone();
        await TaskApi.create({ ...creationParams, timezone });
        this.taskStore.taskList.add([
            {
                authorId: store.authUser.id,
                createdAt: new Date().toISOString(),
                status: TaskStatus.Requested,
                ...creationParams,
                executorIds: creationParams.executorIds ?? [],
                practiceIds: creationParams.practiceIds ?? [],
                timezone,
            },
        ]);
    }

    public async update(id: string, updateParams: TaskUpdateParams): Promise<void> {
        if (Object.keys(updateParams).length > 0) {
            await TaskApi.update(id, updateParams);
            this.taskStore.taskList.update(id, updateParams);
        }
    }

    public async addExecutorsForTasks(ids: string[], executorIds: string[]): Promise<void> {
        await this.mutateTaskExecutors(ids, currentExecutorIds => uniq([...currentExecutorIds, ...executorIds]));
    }

    public async removeExecutorsForTasks(ids: string[], executorIds: string[]): Promise<void> {
        await this.mutateTaskExecutors(ids, currentExecutorIds => difference(currentExecutorIds, executorIds));
    }

    private isOutdatedTasksLoad(loadId: number): boolean {
        return loadId !== this.lastTasksLoadId;
    }

    private async mutateTaskExecutors(
        ids: string[],
        mutate: (currentExecutorIds: string[]) => string[],
    ): Promise<void> {
        const tasksToUpdate = ids
            .map(id => {
                const currentExecutorIds = taskStore.taskList.get(id)?.executorIds ?? [];

                return { id, currentExecutorIds, updatedExecutorIds: mutate(currentExecutorIds) };
            })
            .filter(
                ({ currentExecutorIds, updatedExecutorIds }) => updatedExecutorIds.length !== currentExecutorIds.length,
            );

        await Promise.all(
            tasksToUpdate.map(({ id, updatedExecutorIds }) => TaskApi.update(id, { executorIds: updatedExecutorIds })),
        );

        tasksToUpdate.forEach(({ id, updatedExecutorIds }) => {
            this.taskStore.taskList.update(id, { executorIds: updatedExecutorIds });
        });
    }

    private async loadTaskData(tasks: TaskData[]): Promise<void> {
        const { medicalCaseList } = this.taskStore;

        const chunckedMedicalCaseIds = getUniqNewChunkedIds(
            medicalCaseList.ids,
            tasks.flatMap(it => it.medicalCaseIds),
        );

        const chunkedMedicalCases = await Promise.all(
            chunckedMedicalCaseIds.flatMap(ids => MedicalCaseApi.find({ id: ids.join(',') })),
        );

        const medicalCases = flatten(chunkedMedicalCases);

        const tasksPatientIds = compact(getByFieldName(tasks, 'patientId'));
        const medicalCasesPatientIds = medicalCases.map(it => it.ehrId);

        const patientIds = uniq([...tasksPatientIds, ...medicalCasesPatientIds]);

        await patientService.loadByIds(patientIds);

        this.taskStore.medicalCaseList.add(medicalCases);
    }
}

export const taskService = new TaskService();
